<script setup>
import { computed, inject } from 'vue';
import { openCount } from '../openTrails.js';
import { accentParts, hasLink, styleVars } from './shared.js';
import { heroFocusPosition, heroHasContent, heroOverlayStyle } from '../heroImage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const split = computed(() => props.config.layout === 'split');
const focusStyle = computed(() => ({ objectPosition: heroFocusPosition(props.config) }));
const overlayStyle = computed(() => heroOverlayStyle(props.config));
const titleParts = computed(() => accentParts(props.config.title));
const hasInlineAccent = computed(() => titleParts.value.some((p) => p.accent));

// "18 of 22 trails open", from the same rows the conditions block
// reads. Networks when the org has no public trails yet.
const status = computed(() => {
    if (!props.config.show_status || props.site_data?.show_status === false) return null;
    const rows = (props.site_data?.trails || []).length ? props.site_data.trails : (props.site_data?.networks || []);
    if (!rows.length) return null;
    const open = openCount(rows);
    const noun = (props.site_data?.trails || []).length ? 'trails' : 'networks';
    return { text: `${open} of ${rows.length} ${noun} open`, tone: open === 0 ? 'closed' : (rows.every((r) => r.bucket === 'open') ? 'open' : 'caution') };
});

// Nothing for a visitor when there is nothing to say (heroHasContent);
// the editor canvas keeps the empty hero so there is something to click.
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => heroHasContent(props.config) || Boolean(status.value));
// A split hero with no photo is the words alone for a visitor: an empty
// frame labelled "Hero photo" is an editor placeholder.
const showPhoto = computed(() => split.value && (Boolean(props.config.image) || editing));
</script>

<template>
    <section v-if="hasContent || editing" :id="config.anchor_id || null"
             class="th-block th-hero"
             :class="split ? 'th-hero--split th-tint' : 'th-hero--full'"
             :style="styleVars(config)">
        <template v-if="!split">
            <div class="th-hero__bg" :class="{ 'th-media th-media--dark': config.image }">
                <img v-if="config.image" :src="config.image" alt="" :style="focusStyle">
            </div>
            <div v-if="config.image && overlayStyle" class="th-hero__darken" :style="overlayStyle"></div>
            <div class="th-hero__scrim"></div>
        </template>

        <div class="th-wrap th-hero__inner" :class="{ 'th-split': showPhoto }">
            <div>
                <div v-if="status" class="th-hero__status">
                    <span class="th-dot" :class="`th-dot--${status.tone}`"></span>{{ status.text }}
                </div>
                <p v-else-if="config.eyebrow" class="th-eyebrow th-hero__eyebrow">{{ config.eyebrow }}</p>
                <h1 v-if="titleParts.length || config.title_accent || editing" class="th-h1" :class="{ 'th-hero__title--split': split }">
                    <template v-for="(part, i) in titleParts" :key="i">
                        <span v-if="part.accent" class="th-accent-word">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                    <template v-if="!hasInlineAccent && config.title_accent">
                        {{ ' ' }}<span class="th-accent-word">{{ config.title_accent }}</span>
                    </template>
                </h1>
                <p v-if="config.subtitle" class="th-lede th-hero__lede">{{ config.subtitle }}</p>
                <div v-if="hasLink(config.cta_primary) || hasLink(config.cta_secondary)" class="th-row th-hero__ctas">
                    <a v-if="hasLink(config.cta_primary)" :href="config.cta_primary.href || '#'" class="th-btn th-btn--primary">{{ config.cta_primary.label }}</a>
                    <a v-if="hasLink(config.cta_secondary)" :href="config.cta_secondary.href || '#'"
                       class="th-btn" :class="split ? 'th-btn--ghost' : 'th-btn--ghost-light'">{{ config.cta_secondary.label }}</a>
                </div>
            </div>
            <div v-if="showPhoto" class="th-media th-hero__photo">
                <img v-if="config.image" :src="config.image" alt="" :style="focusStyle">
                <span v-else class="th-media__label">Hero photo</span>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-hero--full {
    min-height: clamp(460px, 72vh, 680px);
    display: flex;
    align-items: flex-end;
    color: var(--block-text, #fff);
    background: var(--block-bg, #2a2f2a);
    overflow: hidden;
}
.th-hero--full .th-hero__bg { position: absolute; inset: 0; border-radius: 0; }
.th-hero__bg img { width: 100%; height: 100%; object-fit: cover; display: block; }
.th-hero__darken { position: absolute; inset: 0; }
.th-hero__scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(10, 12, 10, .7), rgba(10, 12, 10, 0) 65%);
}
.th-hero--full .th-hero__inner {
    position: relative;
    width: 100%;
    padding-bottom: clamp(40px, 7vw, 80px);
    /* The header is sticky above the hero, not over it, so nothing needs
       clearing. The design has none; this only keeps a headline that
       outgrows min-height off the header. */
    padding-top: clamp(32px, 5vw, 56px);
}
.th-hero--full .th-h1 { max-width: 14ch; }
/* On a phone the words fill most of the width and wrap tall, so a
   fixed minimum height left a screen of bare photograph above them
   with the headline pushed to the bottom. The hero is as tall as its
   words plus a strip of photo above them, and the shade under the
   words reaches high enough to cover them all. */
@media (max-width: 640px) {
    .th-hero--full { min-height: 0; }
    .th-hero--full .th-hero__inner { padding-top: clamp(88px, 22vw, 140px); padding-bottom: 36px; }
    .th-hero__scrim { background: linear-gradient(to top, rgba(10, 12, 10, .72), rgba(10, 12, 10, 0) 90%); }
}
.th-hero--full .th-hero__lede { color: var(--text-sub, #e8ebe6); }
.th-hero--full .th-hero__eyebrow { color: var(--text-eyebrow, #fff); }
.th-hero--split .th-hero__inner { padding-top: clamp(40px, 6vw, 80px); padding-bottom: clamp(40px, 6vw, 80px); }
.th-hero__title--split { font-size: clamp(40px, 6vw, 80px); }
.th-hero__lede { margin-bottom: 28px; }
.th-hero__photo { aspect-ratio: 4 / 3.4; }
.th-hero__status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 12px;
    border-radius: 999px;
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .04em;
    text-transform: uppercase;
    margin-bottom: 22px;
}
.th-hero--full .th-hero__status { background: rgba(255, 255, 255, .14); backdrop-filter: blur(6px); }
.th-hero--full .th-hero__status .th-dot--open { background: #4cc46f; }
.th-hero--split .th-hero__status { background: #fff; border: 1px solid var(--th-line); }
.th-hero__status .th-dot { width: 8px; height: 8px; }
</style>
